/**
 * Refine row above the search results (replaces the always-visible scope select and "Advanced
 * filters" button): scope, attachments, unread, date, sender and the "More filters" chip that
 * opens the full filter panel. It edits the same filter state the panel and the query use.
 */
import { useI18n } from '@platform/i18n';
import { Button, cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input } from '@platform/ui';
import { CalendarIcon, ChevronDownIcon, SlidersHorizontalIcon, UserIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import type { SearchRequest } from './api';
import type { SearchScope } from './mail-context';
import { DATE_PRESETS, dateDaysAgo, withFilter, type ActiveFilterChip } from './search-filters';
import { SearchScopeMenu } from './search-scope-menu';

export interface SearchRefineProps {
  scope: SearchScope;
  onScopeChange: (scope: SearchScope) => void;
  filter: SearchRequest['filter'];
  onFilterChange: (filter: SearchRequest['filter']) => void;
  panelOpen: boolean;
  onTogglePanel: () => void;
  /** Other active filters and the query, each with a remove button. */
  chips: readonly ActiveFilterChip[];
  onRemoveChip: (key: string) => void;
}

const chip = 'h-7 shrink-0 gap-1 rounded-full px-2.5 text-xs font-normal';

export function SearchRefine({ scope, onScopeChange, filter, onFilterChange, panelOpen, onTogglePanel, chips, onRemoveChip }: SearchRefineProps) {
  const { t, formatDate } = useI18n();
  const [fromOpen, setFromOpen] = useState(false);
  const [fromDraft, setFromDraft] = useState('');
  const current = filter ?? {};
  const toggle = (key: 'hasAttachments' | 'unread') => onFilterChange(withFilter(current, key, current[key] ? undefined : true));
  const received = current.received;
  const dateLabel = !received
    ? t('webmail.searchRefine.anyDate')
    : received.from && !received.to
      ? t('webmail.searchRefine.since', { value: formatDate(received.from, { dateStyle: 'medium' }) })
      : t('webmail.searchRefine.customDates');

  return (
    <div role="group" aria-label={t('webmail.searchRefine.label')} className="flex shrink-0 flex-nowrap items-center gap-1.5 overflow-x-auto border-b px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <SearchScopeMenu scope={scope} onChange={onScopeChange} />
      <Button type="button" variant={current.hasAttachments ? 'secondary' : 'outline'} aria-pressed={Boolean(current.hasAttachments)} className={chip} onClick={() => toggle('hasAttachments')}>
        {t('webmail.searchRefine.hasAttachments')}
      </Button>
      <Button type="button" variant={current.unread ? 'secondary' : 'outline'} aria-pressed={Boolean(current.unread)} className={chip} onClick={() => toggle('unread')}>
        {t('webmail.searchRefine.unread')}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant={received ? 'secondary' : 'outline'} aria-label={t('webmail.searchRefine.dateLabel', { value: dateLabel })} className={chip}>
            <CalendarIcon aria-hidden className="size-3.5" />
            <span>{dateLabel}</span>
            <ChevronDownIcon aria-hidden className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => onFilterChange(withFilter(current, 'received', undefined))}>{t('webmail.searchRefine.anyDate')}</DropdownMenuItem>
          {DATE_PRESETS.map((preset) => (
            <DropdownMenuItem key={preset.id} onSelect={() => onFilterChange(withFilter(current, 'received', { from: dateDaysAgo(preset.days) }))}>
              {t(`webmail.searchRefine.date.${preset.id}`)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {current.from?.length ? (
        <span className="inline-flex h-7 items-center gap-1 rounded-full border bg-secondary pl-2.5 text-xs">
          <span>{t('webmail.searchAdvanced.filterChip', { name: t('webmail.searchAdvanced.from'), value: current.from.join(', ') })}</span>
          <Button type="button" variant="ghost" size="icon" className="size-6 rounded-full" aria-label={t('webmail.searchAdvanced.removeFilter', { name: t('webmail.searchAdvanced.from') })} onClick={() => onFilterChange(withFilter(current, 'from', undefined))}>
            <XIcon aria-hidden className="size-3.5" />
          </Button>
        </span>
      ) : fromOpen ? (
        <form
          className="flex items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault();
            const people = fromDraft.split(',').map((part) => part.trim()).filter(Boolean);
            if (people.length) onFilterChange(withFilter(current, 'from', people));
            setFromOpen(false);
            setFromDraft('');
          }}
        >
          <Input
            autoFocus
            value={fromDraft}
            onChange={(event) => setFromDraft(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); setFromOpen(false); } }}
            placeholder={t('webmail.searchRefine.fromPlaceholder')}
            aria-label={t('webmail.searchAdvanced.from')}
            className="h-7 w-44 rounded-full px-3 text-xs"
          />
        </form>
      ) : (
        <Button type="button" variant="outline" className={chip} onClick={() => setFromOpen(true)}>
          <UserIcon aria-hidden className="size-3.5" />
          {t('webmail.searchAdvanced.from')}
        </Button>
      )}
      <Button type="button" variant={panelOpen ? 'secondary' : 'outline'} aria-pressed={panelOpen} className={chip} onClick={onTogglePanel}>
        <SlidersHorizontalIcon aria-hidden className="size-3.5" />
        {t('webmail.searchAdvanced.open')}
      </Button>
      {chips.map(({ key, name, shown }) => (
        <span key={key} className={cn('inline-flex h-7 max-w-full items-center gap-0.5 rounded-full border bg-muted pl-2.5 text-xs')}>
          <span className="truncate">{t('webmail.searchAdvanced.filterChip', { name, value: shown })}</span>
          <Button type="button" variant="ghost" size="icon" className="size-6 shrink-0 rounded-full" aria-label={t('webmail.searchAdvanced.removeFilter', { name })} onClick={() => onRemoveChip(key)}>
            <XIcon aria-hidden className="size-3.5" />
          </Button>
        </span>
      ))}
    </div>
  );
}
